import { useTranslation } from "@i18n";
import { useCallback, useMemo, useRef, useState } from "react";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import SlickGrid1, { typedColumns, type GridRowBase, type SlickGrid1Ref } from "@CubeComponents/html/SlickGrid1";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import { includedUsersUrl } from "@Services/dataManager";
import { renderUserTag } from "@CubeComponents/html/userTagDom";
/** A selectable user row in the included-users grid (from `includedUsersController`). */
interface UserRow extends GridRowBase {
  _id: string;
  id?: string;
  fullName?: string;
  email?: string;
  companyName?: string;
  designation?: string;
}

interface AddUsersToMailListProps {
  readonly projectId: string;
  readonly closeModal: () => void;
  readonly excludedUsers: string[];
  readonly handleNext: (selectedUsers: UserRow[]) => void;
}

function AddUsersToMailList({ projectId, closeModal, excludedUsers, handleNext }: AddUsersToMailListProps) {
  const { t } = useTranslation();
  const dataGrid = useRef<SlickGrid1Ref<UserRow> | null>(null);
  const [addUsersModalVisible, setAddUsersModalVisible] = useState(true);
  const [addEnabled, setAddEnabled] = useState(false);
  const [_selectedRowIndexes, setSelectedRowIndexes] = useState<Array<string | undefined>>([]);
  const [activeTab, setActiveTab] = useState("users");

  const query = useMemo(() => {
    if (!projectId) return {};
    return { projectId, source: "projectSetting", params: { excludedUsers } };
  }, [projectId, excludedUsers]);

  const handleCancelAddUsersModal = () => {
    closeModal();
  };

  const GridRowSelectionHandler = () => {
    if (dataGrid.current) {
      const selectedRecords = dataGrid.current?.dataView?.getAllSelectedItems() || [];
      if (selectedRecords.length) {
        setAddEnabled(true);
      } else {
        setAddEnabled(false);
      }
    }
  };

  const fullNameFormatter = (
    _row: number,
    _cell: number,
    value: string | undefined,
    _columnDef: unknown,
    dataContext: UserRow,
  ) => {
    if (dataContext.fullName?.trim()) {
      const container = document.createElement("div");
      renderUserTag(container, { id: dataContext.id });
      return container;
    }
    return value || "";
  };

  const handleNextClick = async () => {
    const modalClose = true;
    if (activeTab === "users") {
      const selectedUsers = dataGrid.current?.dataView?.getAllSelectedItems() || [];
      handleNext(selectedUsers);
      const selectedIds = selectedUsers.map((user) => user.id);
      setSelectedRowIndexes(selectedIds);
    }
    if (modalClose) {
      setAddUsersModalVisible(false);
      closeModal();
    }
  };

  const addUserModalHeader = () => (
    <div className="flex w-full justify-between" data-testid="addUserModalHeader">
      <span className="text-[1.75rem] font-bold text-[var(--primary-text)]" data-testid="selectUsersToSendMail">
        {t("Select Users")}
      </span>
    </div>
  );

  const removeSelectedField = () => {
    setSelectedRowIndexes([]);
    setAddEnabled(false);
  };

  const allUsersTabClickHandler = () => {
    setActiveTab("users");
    // setCreateRuleToggle(false);
    removeSelectedField();
  };

  const columnDefinitions = useMemo(
    () =>
      typedColumns<UserRow>([
        {
          id: "fullName",
          field: "fullName",
          name: t("User Name"),
          minWidth: 150,
          formatter: fullNameFormatter,
        },
        {
          id: "email",
          field: "email",
          name: t("Email"),
          minWidth: 200,
          clipMode: "EllipsisWithTooltip",
        },
        {
          id: "companyName",
          field: "companyName",
          name: t("Org"),
          minWidth: 150,
          clipMode: "EllipsisWithTooltip",
        },
        {
          id: "designation",
          field: "designation",
          name: t("Designation"),
          minWidth: 150,
          clipMode: "EllipsisWithTooltip",
        },
      ]),
    [],
  );

  const handleDataBound = (dataView: unknown): UserRow[] => {
    const { result = [] } = (dataView as { data?: { result?: UserRow[] } })?.data ?? {};
    if (result.length) {
      return result.filter((user) => user.email?.length);
    }
    return result;
  };

  const GridDataList = useCallback(
    () => (
      <SlickGrid1
        ref={dataGrid}
        remoteDataSource={includedUsersUrl}
        query={query}
        columnDefinitions={columnDefinitions}
        gridHeight="100%"
        rowHeight={60}
        onSelectedRowIdsChanged={GridRowSelectionHandler}
        enableFilteringOption
        beforeDataBound={handleDataBound}
        enableSortingOption
        data-testid="allUsersGridList"
      />
    ),
    [query, columnDefinitions],
  );

  const addUserModalContent = () => (
    <>
      <div>
        <div className="bg-[var(--system-colour-43)]">
          <div className="flex h-14 gap-8 border border-solid ps-10 shadow-[0.125rem_0.125rem_0.25rem_0_rgb(25_32_72_/_15%)] [border-image-slice:1] [border-image-source:linear-gradient(92deg,var(--button-light)_30%,rgb(236_243_254_/_15%)_111%)]">
            <div className="flex flex-col gap-2 pt-5">
              <div
                className={
                  activeTab === "users"
                    ? "cursor-pointer text-base font-semibold text-[var(--primary-text)]"
                    : "cursor-pointer text-base text-[var(--primary-text)]"
                }
                onClick={allUsersTabClickHandler}
                data-testid="allUsersTab"
              >
                {t("Users")}
              </div>
              {activeTab === "users" && (
                <div className="h-1 rounded-md bg-gradient-to-r from-[var(--gradient-color)] to-[#3e8ab0]" />
              )}
            </div>
          </div>
          <div className="h-[25rem]" data-testid="allUsersGrid">
            {activeTab === "users" && GridDataList()}
          </div>
        </div>
      </div>
    </>
  );

  const addUserModal = () => (
    <ModalStateDialogComponent
      width="80%"
      allowModalPadding={false}
      headerTitle={addUserModalHeader()}
      modelContent={addUserModalContent()}
      onCancel={handleCancelAddUsersModal}
      footerContent={
        <div className="flex gap-[1.375rem]">
          <ButtonComponent
            buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
            onClick={handleNextClick}
            disabled={!addEnabled}
            data-testid="nextAddUserBtn"
          >
            {t("Add")}
          </ButtonComponent>
          <ButtonComponent
            buttonType={BUTTON_TYPES.BORDER_BUTTON}
            onClick={handleCancelAddUsersModal}
            data-testid="cancelAddUserBtn"
          >
            {t("Discard")}
          </ButtonComponent>
        </div>
      }
      isVisible={addUsersModalVisible}
    />
  );

  return <div>{addUserModal()}</div>;
}

export default AddUsersToMailList;
